/**
 * The entry point.
 *
 * Provider order matters and is not arbitrary:
 *
 *   PersistQueryClientProvider   restores the cache from IndexedDB — must be
 *                                outermost so a cold start paints from disk
 *   AuthProvider                 there is a session, or there is not
 *   TenantProvider               reads the user document, so it needs the session
 *   ConfirmProvider              "are you sure?" for the whole app — above the
 *                                router, so any screen can ask
 *   RouterProvider               route loaders call queries scoped to the tenant
 */
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { AuthProvider } from '@/auth/AuthProvider.tsx';
import { ConfirmProvider } from '@/components/Confirm.tsx';
import { TenantProvider } from '@/tenant/TenantProvider.tsx';
import { CACHE_BUSTER, persister, queryClient } from '@/queryClient.ts';
import { routeTree } from './routeTree.gen.ts';

// For the side effect, and it has to be here rather than in the component that
// asks: Chromium fires `beforeinstallprompt` once, seconds after the page loads,
// and the listener has to already exist. See offline/install.ts.
import '@/offline/install.ts';
import '@/i18n/index.ts';
import '@/styles.css';

const router = createRouter({
  routeTree,
  context: { queryClient },
  // The cache is persisted and Firestore's is too, so a reload has real data to
  // show immediately. Waiting on a loader would blank the screen for no gain.
  defaultPreload: 'intent',
  defaultPendingMs: 0,
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('root');
if (rootElement === null) throw new Error('#root is missing from index.html');

createRoot(rootElement).render(
  <StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister, buster: CACHE_BUSTER, maxAge: 1000 * 60 * 60 * 24 * 14 }}
    >
      <AuthProvider>
        <TenantProvider>
          <ConfirmProvider>
            <RouterProvider router={router} />
          </ConfirmProvider>
        </TenantProvider>
      </AuthProvider>
    </PersistQueryClientProvider>
  </StrictMode>,
);
